import React, { useState, useEffect, useMemo } from 'react';
import NavbarPacker from './NavbarPacker';
import { Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, TextField, Button, Typography, Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import Logger from "./Logger";
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const PackageDetails = () => {
    const [noOfBox, setnoOfBox] = useState('');
    const [noOfPackets, setnoOfPackets] = useState('');
    const [weightOfOrder, setweightOfOrder] = useState('');
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [summaryOrders, setSummaryOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    const [openPrintDialog, setOpenPrintDialog] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const orderNo = sessionStorage.getItem("orderNo");
    const type = sessionStorage.getItem("type");
    const adminEmail = sessionStorage.getItem("adminEmail");
    const navigate = useNavigate();
    const summaryOrdersLength = sessionStorage.getItem('summaryOrdersLength');

    // Initialize Logger
    const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

    const handleSubmit = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`/api/packer/${adminEmail}/${orderNo}/addFields`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type, noOfBox, noOfPackets, weightOfOrder }),
            });

            if (response.status === 200) {
                setSnackbarMessage('Data saved successfully');
                setSnackbarSeverity('success');
                setOpenSnackbar(true);
                setOpenPrintDialog(true);
            }
            else if (response.status === 400) {
                setSnackbarMessage('Data is already saved for it.');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            }
            else {
                setSnackbarMessage('Internal Server Error');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            }
        } catch (err) {
            setError('Failed to submit package details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleSummaryClick = async () => {
            setLoadingDetails(true);

            try {
                const response = await fetch(`/api/packer/${adminEmail}/${orderNo}`);

                if (!response.ok) {
                    throw new Error("Failed to fetch summary orders");
                }

                const data = await response.json();
                setSummaryOrders(data.orders || []);
            } catch (error) {
                logger.log(`Failed to fetch summary orders: ${error.message}`, 'ERROR');
            } finally {
                setLoadingDetails(false);
            }
        };

        handleSummaryClick();
    }, [adminEmail, orderNo, logger]);
    const handleAcceptOrder = async () => {

        try {
            const response = await fetch(`/api/packer/acceptOrder/${adminEmail}/${orderNo}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ orderNo: orderNo })
            });

            if (response.status === 409) {
                setSnackbarMessage('Order already accepted by another packer');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            } else if (response.status === 200) {
                sessionStorage.setItem("orderNo", orderNo);
            } else {
                throw new Error('An unknown error occurred');
            }
        } catch (error) {
            setSnackbarMessage('An error occurred while accepting the order');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
            logger.log(`Error accepting order: ${error.message}`, 'ERROR');
        }
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleOpenDetailsModal = () => {
        setOpenDetailsModal(true);
    };

    return (
        <div>
            <NavbarPacker />
            <div style={{ padding: '20px', marginTop: '50px' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="h4" sx={{ fontSize: "25px" }} gutterBottom>
                        Enter the Package Details
                    </Typography>
                    {type === "Summary" && (
                        <Button variant="contained" onClick={handleOpenDetailsModal} sx={{
                            height: '40px', marginTop: '8px', position: 'absolute', top: '60px', right: '10px', backgroundColor: '#07a6ef', color: '#fff',
                            '&:hover': {
                                backgroundColor: 'primary',
                            }
                        }}>
                            Details
                        </Button>
                    )}
                </div>
                <Typography variant="body1" gutterBottom style={{ display: 'flex', justifyContent: 'center' }}>
                    {type} No. : {orderNo}
                </Typography>

                <Card sx={{ padding: '20px', width: '50%', marginLeft: "25%" }}>
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        setOpenConfirmDialog(true); // Open confirmation dialog
                    }} autocomplete="off" >
                        <Card sx={{ marginBottom: '20px' }}>
                            <CardContent>
                                <TextField
                                    label="Number of Boxes"
                                    variant="outlined"
                                    fullWidth
                                    value={noOfBox}
                                    onChange={(e) => {
                                        // Only allow non-negative values
                                        const value = e.target.value;
                                        if (value >= 0 || value === '') {
                                            setnoOfBox(value);
                                        }
                                    }}
                                    type="number"
                                    required
                                    sx={{
                                        '& input[type=number]': {
                                            MozAppearance: 'textfield', // Firefox
                                        },
                                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                    }}
                                />
                            </CardContent>
                        </Card>

                        <Card sx={{ marginBottom: '20px' }}>
                            <CardContent>
                                <TextField
                                    label="Number of Packets"
                                    variant="outlined"
                                    fullWidth
                                    value={noOfPackets}
                                    onChange={(e) => {
                                        // Only allow non-negative values
                                        const value = e.target.value;
                                        if (value >= 0 || value === '') {
                                            setnoOfPackets(value);
                                        }
                                    }}
                                    type="number"
                                    required
                                    sx={{
                                        '& input[type=number]': {
                                            MozAppearance: 'textfield', // Firefox
                                        },
                                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                    }}
                                />
                            </CardContent>
                        </Card>

                        <Card sx={{ marginBottom: '20px' }}>
                            <CardContent>
                                <TextField
                                    label="Weight of Order"
                                    variant="outlined"
                                    fullWidth
                                    value={weightOfOrder}
                                    onChange={(e) => {
                                        // Only allow non-negative values
                                        const value = e.target.value;
                                        if (value >= 0 || value === '') {
                                            setweightOfOrder(value);
                                        }
                                    }}
                                    type="number"
                                    required
                                    sx={{
                                        '& input[type=number]': {
                                            MozAppearance: 'textfield', // Firefox
                                        },
                                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                            WebkitAppearance: 'none',
                                            margin: 0,
                                        },
                                    }}
                                />
                            </CardContent>
                        </Card>

                        <Button type="submit" variant="contained" color="primary" sx={{ width: '100%', marginTop: '-2px !important' }} disabled={loading}>
                            {loading ? 'Submitting...' : 'Submit'}
                        </Button>
                    </form>

                    {error && (
                        <Typography color="error" sx={{ marginTop: '20px' }}>
                            {error}
                        </Typography>
                    )}
                </Card>
            </div>

            <Dialog open={openConfirmDialog} onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    setOpenConfirmDialog(false);
                }
            }}
                disableEscapeKeyDown>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to submit the package details?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDialog(false)} color="secondary">No</Button>
                    <Button onClick={() => { setOpenConfirmDialog(false); handleAcceptOrder(); handleSubmit(); }} color="primary">Yes</Button>
                </DialogActions>
            </Dialog>

            {/* Print Label Confirmation Dialog */}
            <Dialog
                open={openPrintDialog}
                onClose={(event, reason) => {
                    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                        setOpenPrintDialog(false);
                    }
                }}
            >
                <DialogTitle>Print Label</DialogTitle>
                <DialogContent>
                    <DialogContentText>Do you want to print the label now?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setOpenPrintDialog(false);
                        navigate('/packerLanding');
                    }} color="secondary">No</Button>
                    <Button onClick={() => {
                        setOpenPrintDialog(false);
                        navigate('/printDetails');
                    }} color="primary">Yes</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDetailsModal} onClose={() => setOpenDetailsModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{type} Details</DialogTitle>
                <DialogContent sx={{ color: 'grey' }}>Total Orders: {summaryOrdersLength}</DialogContent>
                <DialogContent sx={{ marginTop: '-20px' }}>
                    {loadingDetails ? (
                        <CircularProgress />
                    ) : (
                        <div>
                            {summaryOrders.length > 0 ? (
                                summaryOrders.map((order, index) => (
                                    <div key={index}>
                                        <Typography variant="body1">Order No.:{order.Order_No}</Typography>
                                    </div>
                                ))
                            ) : (
                                <Typography variant="body1" color="textSecondary">No order details available.</Typography>
                            )}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDetailsModal(false)} color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default PackageDetails;
